import { PermissionService } from 'app/services/auth/permission.service';
import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { RegistrationService } from 'app/entities/registration/registration.service';
import { ModalService } from 'app/components/modal/modal.service';
import { SortDirection } from 'app/services/utils.service';
import { SportService } from 'app/services/sport/sport.service';
import { Category, Event } from 'app/entities/event/event.types';
import { CategoryReport } from 'app/entities/event/event-report.types';
import { EnrichedRegistrationWithMembers } from 'app/entities/registration/registration.types';
import { Subscription } from 'rxjs';

export enum CountryDisplayMode {
  COUNTRY = 'COUNTRY',
  CLUB = 'CLUB',
  COUNTRY_AND_CLUB = 'COUNTRY_AND_CLUB',
}

@Component({
  selector: 'category-registrations',
  templateUrl: 'category-registrations.html',
  standalone: false
})
export class CategoryRegistrationsComponent implements OnInit, AfterViewInit {
  @Input() event: Event;
  @Input() category: Category;
  @Input() categoryReport: CategoryReport;
  @ViewChild('desktopIndicator') desktopIndicator: ElementRef;
  isCollapsed = true;
  loading = false;
  registrationsRendered = false;
  registrations: EnrichedRegistrationWithMembers[] = [];
  waitingListRegistrations = [];
  ascending = true;
  waitingListSelectionEmpty = true;
  externalRegistrationsEnabled = false;
  desktopVersionVisible = true;
  acceptedRegistrationsCount: number;
  waitingListRegistrationsCount: number;
  clubColumnTitleKey: string;
  defaultSortDirection = SortDirection.ASC;
  teamCompetitionEnabled: boolean;
  private countryDisplayMode: CountryDisplayMode;
  private selectedWaitingListRegistrations = new Set<number>();
  private translationChangeSubscriber: Subscription;

  constructor(
    private regService: RegistrationService,
    private permissionService: PermissionService,
    private modalService: ModalService,
    private sportService: SportService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.acceptedRegistrationsCount = this.categoryReport?.acceptedCount;
    this.waitingListRegistrationsCount = this.categoryReport?.waitingListCount;

    if (!this.categoryReport) {
      this.loadResults();
    }

    if (this.event.settings?.externalRegistrationsEnabled) {
      this.externalRegistrationsEnabled = true;
      this.updateCountriesForRegistrations();
    }

    this.countryDisplayMode =
      this.event.settings?.countryDisplayMode ||
      (this.externalRegistrationsEnabled ? CountryDisplayMode.COUNTRY : CountryDisplayMode.CLUB);
    this.clubColumnTitleKey =
      this.countryDisplayMode === CountryDisplayMode.COUNTRY ? 'registration.country' : 'registration.clubName';
    this.teamCompetitionEnabled =
      this.event.settings?.teamCompetitionEnabled &&
      this.sportService.getCategoryAttribute(this.category, 'teamCompetitionEnabled', false);
  }

  ngAfterViewInit() {
    this.updateResponsiveVisibility();
  }

  toggleRowExpand() {
    this.updateResponsiveVisibility();
    if (this.registrationsRendered) {
      this.isCollapsed = !this.isCollapsed;
    } else {
      this.registrationsRendered = true;
      this.loadResults().then(() => {
        this.isCollapsed = !this.isCollapsed;
      });
    }
  }

  updateResponsiveVisibility() {
    this.desktopVersionVisible = this.desktopIndicator.nativeElement.offsetParent !== null;
  }

  private loadResults() {
    this.loading = true;
    this.registrations = [];
    this.waitingListRegistrations = [];
    const p1 = this.regService.getForCategory(this.category.id).then((results) => {
      this.acceptedRegistrationsCount = 0;
      this.registrations = [];
      results.forEach((res) => {
        this.registrations.push({ ...res, countryOrClub: '' });
        if (res.status === 'ACCEPTED') {
          this.acceptedRegistrationsCount++;
        }
      });
      this.updateCountriesForRegistrations();
    });
    const p2 = this.regService.getWaitingListForCategory(this.category.id).then((results) => {
      results.forEach((res) => {
        this.waitingListRegistrations.push(res);
      });
      this.updateCountriesForRegistrations();
      this.waitingListRegistrationsCount = this.waitingListRegistrations.length;
    });
    return Promise.all([p1, p2]).finally(() => (this.loading = false));
  }

  updateCountriesForRegistrations() {
    const uniqueCountryCodes = new Set<string>();
    const allRegistrations = this.registrations.concat(this.waitingListRegistrations);
    allRegistrations
      .filter((reg) => reg.extraAttributes?.countryCode)
      .map((reg) => `country.${reg.extraAttributes.countryCode}.name`)
      .forEach((key) => uniqueCountryCodes.add(key));
    allRegistrations.forEach((reg) => (reg.countryOrClub = reg.clubName));

    this.translationChangeSubscriber?.unsubscribe();

    if (uniqueCountryCodes.size === 0) {
      return;
    }
    this.translationChangeSubscriber = this.translate.stream(Array.from(uniqueCountryCodes)).subscribe({
      next: (translationsByKey) => {
        allRegistrations.forEach((reg) => {
          if (reg.extraAttributes?.countryCode) {
            if (this.countryDisplayMode === CountryDisplayMode.CLUB) {
              reg.countryOrClub = reg.clubName;
            } else if (this.countryDisplayMode === CountryDisplayMode.COUNTRY) {
              const countryCode = reg.extraAttributes?.countryCode || 'FIN';
              reg.countryOrClub = translationsByKey[`country.${countryCode}.name`];
            } else {
              const countryCode = reg.extraAttributes?.countryCode || 'FIN';
              reg.countryOrClub = `${reg.clubName} (${countryCode})`;
            }
          } else {
            reg.countryOrClub = reg.clubName;
          }
        });
      },
    });
  }

  hasAdminAccessToEvent() {
    return this.permissionService.hasAdminAccessToEvent(this.event);
  }

  acceptRegistrations() {
    if (this.selectedWaitingListRegistrations.size > 0) {
      this.modalService.showLocalizedYesNoDialog(
        'event.acceptRegistrationsDialog.title',
        'event.acceptRegistrationsDialog.body',
        () => this.doAcceptRegistrations()
      );
    }
  }

  doAcceptRegistrations() {
    const selectedRegistrationIds = Array.from(this.selectedWaitingListRegistrations);
    this.regService.acceptWaitingListRegistrations(this.event.id, selectedRegistrationIds).then(() => {
      this.selectedWaitingListRegistrations.clear();
      this.waitingListSelectionEmpty = this.selectedWaitingListRegistrations.size === 0;
      this.loadResults();
    });
  }

  waitingListRowSelectionChanged({ registration, selected }) {
    if (selected) {
      this.selectedWaitingListRegistrations.add(registration.id);
    } else {
      this.selectedWaitingListRegistrations.delete(registration.id);
    }
    this.waitingListSelectionEmpty = this.selectedWaitingListRegistrations.size === 0;
  }
}
